'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { CuteSpinner } from '@/components/common/CuteSpinner';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select';
import { formatContextTokens, formatTweetLimit } from '@/lib/ai/formatters';
import { cn } from '@/lib/utils';
import { Z_LAYERS } from '@/styles/z-layers';
import type { ModelPickerOption } from '@/types/ai';
import { ProviderLogo } from './provider-logos';
export interface ModelPickerProperties {
  className?: string | undefined;
  disabled?: boolean | undefined;
  dropdownProps?: {
    showProvider?: boolean | undefined;
    showProviderLogo?: boolean | undefined;
  } | undefined;
  emptyMessage?: string | undefined;
  error?: string | null | undefined;
  getDisplayLabel?: ((option: ModelPickerOption) => string) | undefined;
  getOptionValue?: ((option: ModelPickerOption) => string) | undefined;
  helperText?: ReactNode | undefined;
  isLoading?: boolean | undefined;
  label?: ReactNode | undefined;
  onChange: (value: string) => void;
  options: ModelPickerOption[];
  placeholder?: string | undefined;
  showContextTokens?: boolean | undefined;
  showProvider?: boolean | undefined;
  showProviderLogo?: boolean | undefined;
  showTier?: boolean | undefined;
  showTweetLimit?: boolean | undefined;
  triggerClassName?: string | undefined;
  value: string | null;
}
export function ModelPicker(t0) {
  const $ = _c(68);
  const {
    options,
    value,
    onChange,
    disabled: t1,
    placeholder: t2,
    isLoading: t3,
    error: t4,
    emptyMessage: t5,
    label,
    helperText,
    className,
    triggerClassName,
    showContextTokens: t6,
    showTweetLimit: t7,
    showProvider: t8,
    showTier: t9,
    showProviderLogo: t10,
    getOptionValue,
    getDisplayLabel,
    dropdownProps
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const placeholder = t2 === undefined ? "Select a model" : t2;
  const isLoading = t3 === undefined ? false : t3;
  const error = t4 === undefined ? null : t4;
  const emptyMessage = t5 === undefined ? "No models available." : t5;
  const showContextTokens = t6 === undefined ? true : t6;
  const showTweetLimit = t7 === undefined ? false : t7;
  const showProvider = t8 === undefined ? true : t8;
  const showTier = t9 === undefined ? true : t9;
  const showProviderLogo = t10 === undefined ? true : t10;
  const effectiveGetValue = getOptionValue ?? _temp;
  const effectiveGetLabel = getDisplayLabel ?? _temp2;
  let T0;
  let T1;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  if ($[0] !== className || $[1] !== disabled || $[2] !== dropdownProps?.showProvider || $[3] !== dropdownProps?.showProviderLogo || $[4] !== effectiveGetLabel || $[5] !== effectiveGetValue || $[6] !== emptyMessage || $[7] !== isLoading || $[8] !== label || $[9] !== onChange || $[10] !== options || $[11] !== placeholder || $[12] !== showContextTokens || $[13] !== showProvider || $[14] !== showProviderLogo || $[15] !== showTier || $[16] !== showTweetLimit || $[17] !== triggerClassName || $[18] !== value) {
    const seenOptionValues = new Set();
    const dedupedOptions = [];
    for (const option_1 of options) {
      const optionValue = effectiveGetValue(option_1);
      if (seenOptionValues.has(optionValue)) {
        continue;
      }
      seenOptionValues.add(optionValue);
      dedupedOptions.push(option_1);
    }
    const optionPool = dedupedOptions;
    const selectedOption = optionPool.find(option_3 => effectiveGetValue(option_3) === value) ?? options.find(option_2 => effectiveGetValue(option_2) === value) ?? null;
    const selectValue = selectedOption ? effectiveGetValue(selectedOption) : value ?? "";
    const triggerLabel = selectedOption ? effectiveGetLabel(selectedOption) : placeholder;
    const isPlaceholder = !selectedOption;
    const hasOptions = optionPool.length > 0;
    if ($[33] !== className) {
      t21 = cn("flex flex-col gap-2", className);
      $[33] = className;
      $[34] = t21;
    } else {
      t21 = $[34];
    }
    if ($[35] !== label) {
      t22 = label != null && label !== "" ? <Label className="font-medium">{label}</Label> : null;
      $[35] = label;
      $[36] = t22;
    } else {
      t22 = $[36];
    }
    T1 = Select;
    t17 = selectValue;
    t18 = onChange;
    t19 = disabled || isLoading || !hasOptions;
    const t23 = isPlaceholder && "text-muted-foreground";
    let t24;
    if ($[37] !== t23 || $[38] !== triggerClassName) {
      t24 = cn("flex h-11 w-full items-center justify-between gap-3 rounded-xl border border-border/60 bg-background px-4 text-sm shadow-sm", t23, triggerClassName);
      $[37] = t23;
      $[38] = triggerClassName;
      $[39] = t24;
    } else {
      t24 = $[39];
    }
    let t25;
    if ($[40] !== isLoading) {
      t25 = isLoading ? <CuteSpinner size={16} className="flex-shrink-0 border-muted-foreground/30 border-t-muted-foreground" /> : null;
      $[40] = isLoading;
      $[41] = t25;
    } else {
      t25 = $[41];
    }
    t20 = <SelectTrigger className={t24}><span className="flex-1 truncate text-left font-medium">{triggerLabel}</span>{t25}</SelectTrigger>;
    T0 = SelectContent;
    t11 = "popper";
    t12 = 6;
    t13 = "start";
    t14 = "min-w-[var(--radix-select-trigger-width)] rounded-lg border border-border/50 bg-background shadow-xl z-layer-flyout";
    if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = {
        width: "max(var(--radix-select-trigger-width), min(20.664rem, calc(100vw - 32px)))",
        zIndex: Z_LAYERS.flyout
      };
      $[42] = t15;
    } else {
      t15 = $[42];
    }
    t16 = hasOptions ? optionPool.map(option_4 => {
      const optionValue_0 = effectiveGetValue(option_4);
      const contextLabel = showContextTokens ? formatContextTokens(option_4.contextWindowTokens) : null;
      const tweetLimitLabel = showTweetLimit ? formatTweetLimit(option_4.tweetLimit) : null;
      return <SelectItem key={optionValue_0} value={optionValue_0} textValue={effectiveGetLabel(option_4)}><div className="flex w-full items-start gap-3">{dropdownProps?.showProviderLogo ?? showProviderLogo ? <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md border border-border/40 bg-foreground text-white dark:bg-black/85"><ProviderLogo provider={option_4.provider} className="h-4 w-4" tone="dark" /></div> : null}<div className="flex flex-1 flex-col gap-1"><div className="flex w-full items-center justify-between gap-2"><span className="min-w-0 truncate font-medium leading-tight">{effectiveGetLabel(option_4)}</span>{contextLabel ? <Badge variant="secondary" className="shrink-0 whitespace-nowrap text-right text-[10px] font-semibold uppercase tracking-wide">{contextLabel}</Badge> : null}</div>{((dropdownProps?.showProvider ?? showProvider) || showTier || tweetLimitLabel) && <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">{dropdownProps?.showProvider ?? showProvider ? <span className="font-medium capitalize">{option_4.provider}</span> : null}{showTier ? <span className="rounded-full border border-border/60 px-2 py-0.5 text-[10px] uppercase tracking-wide">{option_4.tier}</span> : null}{tweetLimitLabel ? <span>{tweetLimitLabel}</span> : null}</div>}</div></div></SelectItem>;
    }) : <div className="px-3 py-2 text-sm text-muted-foreground">{emptyMessage}</div>;
    $[0] = className;
    $[1] = disabled;
    $[2] = dropdownProps?.showProvider;
    $[3] = dropdownProps?.showProviderLogo;
    $[4] = effectiveGetLabel;
    $[5] = effectiveGetValue;
    $[6] = emptyMessage;
    $[7] = isLoading;
    $[8] = label;
    $[9] = onChange;
    $[10] = options;
    $[11] = placeholder;
    $[12] = showContextTokens;
    $[13] = showProvider;
    $[14] = showProviderLogo;
    $[15] = showTier;
    $[16] = showTweetLimit;
    $[17] = triggerClassName;
    $[18] = value;
    $[19] = T0;
    $[20] = T1;
    $[21] = t11;
    $[22] = t12;
    $[23] = t13;
    $[24] = t14;
    $[25] = t15;
    $[26] = t16;
    $[27] = t17;
    $[28] = t18;
    $[29] = t19;
    $[30] = t20;
    $[31] = t21;
    $[32] = t22;
  } else {
    T0 = $[19];
    T1 = $[20];
    t11 = $[21];
    t12 = $[22];
    t13 = $[23];
    t14 = $[24];
    t15 = $[25];
    t16 = $[26];
    t17 = $[27];
    t18 = $[28];
    t19 = $[29];
    t20 = $[30];
    t21 = $[31];
    t22 = $[32];
  }
  let t23;
  if ($[43] !== T0 || $[44] !== t11 || $[45] !== t12 || $[46] !== t13 || $[47] !== t14 || $[48] !== t15 || $[49] !== t16) {
    t23 = <T0 position={t11} sideOffset={t12} align={t13} className={t14} style={t15}>{t16}</T0>;
    $[43] = T0;
    $[44] = t11;
    $[45] = t12;
    $[46] = t13;
    $[47] = t14;
    $[48] = t15;
    $[49] = t16;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== T1 || $[52] !== t17 || $[53] !== t18 || $[54] !== t19 || $[55] !== t20 || $[56] !== t23) {
    t24 = <T1 value={t17} onValueChange={t18} disabled={t19}>{t20}{t23}</T1>;
    $[51] = T1;
    $[52] = t17;
    $[53] = t18;
    $[54] = t19;
    $[55] = t20;
    $[56] = t23;
    $[57] = t24;
  } else {
    t24 = $[57];
  }
  let t25;
  if ($[58] !== error) {
    t25 = error ? <p className="text-xs text-destructive">{error}</p> : null;
    $[58] = error;
    $[59] = t25;
  } else {
    t25 = $[59];
  }
  let t26;
  if ($[60] !== helperText) {
    t26 = helperText == null ? null : <p className="text-xs text-muted-foreground">{helperText}</p>;
    $[60] = helperText;
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  let t27;
  if ($[62] !== t21 || $[63] !== t22 || $[64] !== t24 || $[65] !== t25 || $[66] !== t26) {
    t27 = <div className={t21}>{t22}{t24}{t25}{t26}</div>;
    $[62] = t21;
    $[63] = t22;
    $[64] = t24;
    $[65] = t25;
    $[66] = t26;
    $[67] = t27;
  } else {
    t27 = $[67];
  }
  return t27;
}
function _temp2(option_0) {
  return option_0.displayName;
}
function _temp(option) {
  return option.id;
}